import moment from 'moment';

import { localStorageGet, localStorageRemove, localStorageSet } from '~/app/helpers/localStorage';

import { ConnectivityListener, ConnectivitySnapshot, OfflineStatus } from './types';

const offlineDuration: number = parseInt(process.env.REACT_APP_OFFLINE_DURATION || '0', 10);

function initialManualOfflineTime(): string | null {
  const stored = localStorageGet('offline-datetime');
  if (!stored) {
    return null;
  }
  const timeDiff = moment(stored).diff(moment(), 'seconds');
  if (timeDiff <= 0) {
    localStorageRemove('offline-datetime');
    return null;
  }
  return stored;
}

function initialStatus(): OfflineStatus {
  return initialManualOfflineTime() ? 2 : 0;
}

export class ConnectivityService {
  private snapshot: ConnectivitySnapshot = {
    status: initialStatus(),
    networkStatus: 0,
    time: initialManualOfflineTime(),
  };

  private listeners = new Set<ConnectivityListener>();

  getSnapshot(): ConnectivitySnapshot {
    return { ...this.snapshot };
  }

  isOnline(): boolean {
    return this.snapshot.status === 0;
  }

  isNetworkOnline(): boolean {
    return this.snapshot.networkStatus !== 1;
  }

  /** Axios / fetch "Network Error" — device lost reachability to the API. */
  notifyNetworkError(): void {
    this.setNetworkStatus(1);
  }

  /** Recovery probe succeeded (e.g. 10s poll while networkStatus was 1). */
  notifyNetworkRecovered(): void {
    this.setNetworkStatus(0);
  }

  /**
   * When manual offline has a timed expiry, revert to network-derived status.
   * No-op while manual offline is indefinite (REACT_APP_OFFLINE_DURATION === 0).
   */
  refreshManualOfflineExpiry(): void {
    if (this.snapshot.status !== 2 || !this.snapshot.time || offlineDuration === 0) {
      return;
    }
    if (moment(this.snapshot.time).diff(moment(), 'seconds') > 0) {
      return;
    }
    localStorageRemove('offline-datetime');
    this.snapshot = {
      ...this.snapshot,
      status: this.snapshot.networkStatus,
      time: null,
    };
    this.emit();
  }

  subscribe(listener: ConnectivityListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const snap = this.getSnapshot();
    this.listeners.forEach((l) => l(snap));
  }

  setManualStatus(payload: OfflineStatus): void {
    const datetime =
      payload === 2
        ? moment().add(offlineDuration, 'seconds').format('YYYY-MM-DD HH:mm:ss')
        : null;
    if (payload === 2 && datetime) {
      localStorageSet('offline-datetime', datetime);
    } else {
      localStorageRemove('offline-datetime');
    }
    const effectiveStatus: OfflineStatus = payload === 2 ? 2 : this.snapshot.networkStatus;
    this.snapshot = {
      ...this.snapshot,
      status: effectiveStatus,
      time: datetime,
    };
    this.emit();
  }

  setNetworkStatus(payload: OfflineStatus): void {
    const { status, time } = this.snapshot;
    const manualActive =
      status === 2 &&
      (offlineDuration === 0 || moment(time).diff(moment(), 'seconds') >= 0);

    this.snapshot = {
      ...this.snapshot,
      networkStatus: payload,
      ...(manualActive
        ? {}
        : {
            status: payload,
            time: null,
          }),
    };
    this.emit();
  }

  /** Cordova / navigator.connection probe */
  applyNavigatorConnection(): void {
    const offline = navigator['connection']?.type === 'none';
    this.setNetworkStatus(offline ? 1 : 0);
  }
}

export const connectivityService = new ConnectivityService();
